import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import { parseReactProps } from '~/components/legacy/reactProps.js';

function formatCurrency(locale, amount) {
  return new Intl.NumberFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2
  }).format(Number(amount || 0));
}

function formatMonthlyPrice(locale, amount) {
  return formatCurrency(locale, amount);
}

function runtimeLabel(i18n, runtimeMonths) {
  return runtimeMonths === 1
    ? `1 ${i18n.monthLabel}`
    : `${runtimeMonths} ${i18n.monthsLabel}`;
}

function tierKeyForPlan(planKey) {
  if (String(planKey).includes('_max_')) return 'max';
  if (String(planKey).includes('_plus_')) return 'plus';
  return 'basic';
}

function tierIndex(tierKey) {
  return {
    basic: 0,
    plus: 1,
    max: 2
  }[tierKey] ?? 0;
}

function findRuntime(plan, runtimeMonths) {
  return Array.isArray(plan?.runtimes)
    ? plan.runtimes.find((runtime) => Number(runtime.runtimeMonths) === Number(runtimeMonths)) || null
    : null;
}

function sortedPlansForFamily(family) {
  return Array.isArray(family?.plans)
    ? [...family.plans].sort((left, right) => Number(left.displayIndex || 0) - Number(right.displayIndex || 0))
    : [];
}

function seatSummary(i18n, runtime, seatPriceConfigured = true) {
  if (!runtime) {
    return {
      primary: i18n.unavailableForRuntime,
      secondary: ''
    };
  }

  const includedSeats = Number(runtime.includedSeats || 0);
  const maxSeats = seatPriceConfigured ? Number(runtime.maxSeats || 0) : includedSeats;

  if (!runtime.workspace || maxSeats <= 0) {
    return {
      primary: i18n.noSeatIncluded,
      secondary: i18n.noAdditionalSeats
    };
  }

  const primary = includedSeats === 1
    ? `1 ${i18n.seatLabel} (${i18n.minimumSeatLabel})`
    : `${includedSeats} ${i18n.seatsLabel} (${i18n.minimumSeatLabel})`;

  const secondary = maxSeats > includedSeats
    ? `${i18n.bookableUpToLabel} ${maxSeats} ${i18n.seatsLabel}`
    : i18n.noAdditionalSeats;

  return { primary, secondary };
}

function buildCheckoutPath(checkoutPath, totalSeats) {
  if (!checkoutPath) {
    return null;
  }

  if (!(Number(totalSeats) > 0)) {
    return checkoutPath;
  }

  const separator = checkoutPath.includes('?') ? '&' : '?';
  return `${checkoutPath}${separator}total_workspace_seats=${encodeURIComponent(totalSeats)}`;
}

function SubscriptionTierOverview(props) {
  const locale = props.locale || 'de';
  const families = Array.isArray(props.families) ? props.families : [];
  const showAnnualBenefitHints = Boolean(props.showAnnualBenefitHints);
  const showWorkspaceBenefitHints = Boolean(props.showWorkspaceBenefitHints);
  const runtimeOptions = Array.from(new Set(
    families.flatMap((family) => (
      Array.isArray(family?.plans)
        ? family.plans.flatMap((plan) => Array.isArray(plan?.runtimes) ? plan.runtimes.map((runtime) => Number(runtime.runtimeMonths)) : [])
        : []
    ))
  )).sort((left, right) => left - right);
  const availableRuntimeOptions = runtimeOptions.length > 0 ? runtimeOptions : [3, 12];
  const defaultRuntimeMonths = availableRuntimeOptions.includes(12)
    ? 12
    : availableRuntimeOptions[availableRuntimeOptions.length - 1];
  const [selectedRuntimeMonths, setSelectedRuntimeMonths] = useState(defaultRuntimeMonths);
  const [selectedPlanByFamily, setSelectedPlanByFamily] = useState(() => (
    families.reduce((result, family) => {
      const firstPlan = sortedPlansForFamily(family)[0];
      if (firstPlan) {
        result[family.key] = firstPlan.key;
      }
      return result;
    }, {})
  ));
  const [selectedSeatsByKey, setSelectedSeatsByKey] = useState(() => (
    families.reduce((result, family) => {
      const firstPlan = sortedPlansForFamily(family)[0];
      const firstRuntime = findRuntime(firstPlan, defaultRuntimeMonths) || firstPlan?.runtimes?.[0] || null;
      if (firstRuntime) {
        const includedSeats = Number(firstRuntime.includedSeats || 0);
        result[family.key] = includedSeats > 0 ? includedSeats : 0;
      }
      return result;
    }, {})
  ));

  const i18n = {
    heading: 'Another plan view',
    subheading: 'Switch between the included tiers per family.',
    runtimeSwitchLabel: 'Term',
    monthLabel: 'month',
    monthsLabel: 'months',
    billedEvery: 'billed every',
    basicLabel: 'Basic',
    plusLabel: 'Plus',
    maxLabel: 'Max',
    includedLabel: 'inklusive',
    minimumSeatLabel: 'Mindestumfang',
    seatPriceHint: 'Je zusätzlichem Seat oberhalb des Inklusivumfangs fallen %{price} pro Monat an.',
    seatPriceUnavailable: 'Zusätzliche Seats sind für diesen Tarif aktuell nicht eingerichtet und können nicht hinzugebucht werden.',
    workspaceCheckoutHint: locale === 'de' ? 'Seats richten Sie im Checkout ein.' : 'Choose your seats in checkout.',
    noSeatIncluded: 'Kein Seat enthalten',
    noAdditionalSeats: 'keine Zusatzseats',
    bookableUpToLabel: 'buchbar bis',
    seatLabel: 'Seat',
    seatsLabel: 'Seats',
    seatsPickerLabel: 'Seats auswählen',
    soloBasicAnnualBenefitTeaser: 'Secure the annual plan benefit: downloads incl. track variants',
    soloBasicAnnualBenefitIncluded: 'Included on the annual plan: downloads incl. track variants',
    soloAnnualBenefitTeaser: 'Annual plan benefit: personal workspace and downloads incl. track variants',
    soloAnnualBenefitIncluded: 'Included on the annual plan: personal workspace and downloads incl. track variants',
    soloWorkspaceBenefitTitle: 'Your personal workspace access is included',
    soloWorkspaceBenefitDetail: 'Save project licenses, keep track of ongoing projects, and download tracks incl. variants directly in the workspace.',
    seatBenefitTitle: '1 seat = 1 full access seat for one team member',
    seatBenefitDetail: 'with its own login, projects, and licensing access',
    checkoutCta: 'Abo auswählen',
    detailCta: 'View the %{plan} subscription tier',
    unavailableForRuntime: 'Not available for this term yet.',
    ...props.i18n
  };

  function selectPlan(familyKey, planKey) {
    setSelectedPlanByFamily((prevState) => ({
      ...prevState,
      [familyKey]: planKey
    }));
  }

  function selectSeats(selectionKey, totalSeats) {
    setSelectedSeatsByKey((prevState) => ({
      ...prevState,
      [selectionKey]: Number(totalSeats)
    }));
  }

  const hasHeaderCopy = Boolean(i18n.heading || i18n.subheading);
  const cardEntries = families.map((family) => ({
    family,
    plans: sortedPlansForFamily(family),
    activePlan: sortedPlansForFamily(family).find((plan) => plan.key === selectedPlanByFamily[family.key]) || sortedPlansForFamily(family)[0],
    selectionKey: family.key
  }));
  const gridClassName = cardEntries.length <= 2
    ? 'grid grid--2-col grid--1-col-md-down subscription-tier-overview__grid'
    : 'grid grid--3-col grid--1-col-md-down subscription-tier-overview__grid';

  return (
    <div className='subscription-tier-overview' data-role='subscription-tier-overview'>
      <div className={hasHeaderCopy ? 'subscription-tier-overview__head' : 'subscription-tier-overview__head subscription-tier-overview__head--compact'}>
        {hasHeaderCopy ? (
          <div>
            {i18n.heading ? <h2 className='text-like-h3 margin-bottom8'>{i18n.heading}</h2> : null}
            {i18n.subheading ? <p className='text-color-secondary margin-bottom0'>{i18n.subheading}</p> : null}
          </div>
        ) : null}
        <div className='subscription-tier-overview__toolbar-controls'>
          <span className='subscription-tier-overview__toolbar-label'>{i18n.runtimeSwitchLabel}</span>
          <div className='pricing-licenses__top-switch' role='tablist' aria-label={i18n.runtimeSwitchLabel}>
            {availableRuntimeOptions.map((runtimeMonths) => (
              <button
                key={runtimeMonths}
                type='button'
                className={`pricing-licenses__top-switch-link ${selectedRuntimeMonths === runtimeMonths ? 'pricing-licenses__top-switch-link--active' : ''}`}
                onClick={() => setSelectedRuntimeMonths(runtimeMonths)}
                role='tab'
                aria-selected={selectedRuntimeMonths === runtimeMonths}
              >
                {runtimeLabel(i18n, runtimeMonths)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={gridClassName}>
        {cardEntries.map(({ family, plans, activePlan, selectionKey }) => {
          const activeRuntime = activePlan ? findRuntime(activePlan, selectedRuntimeMonths) : null;
          const activeTierKey = activePlan ? tierKeyForPlan(activePlan.key) : 'basic';
          const includedSeats = Number(activeRuntime?.includedSeats || 0);
          const maxSeats = Number(activeRuntime?.maxSeats || 0);
          const seatPriceConfigured = activeRuntime?.seatPriceConfigured === true;
          const selectedSeatsRaw = Number(selectedSeatsByKey[selectionKey] || includedSeats || 0);
          const effectiveSeats = activeRuntime?.workspace
            ? Math.min(Math.max(selectedSeatsRaw || includedSeats, includedSeats), maxSeats || includedSeats)
            : 0;
          const seatInfo = seatSummary(i18n, activeRuntime, seatPriceConfigured);
          const selectableMaxSeats = seatPriceConfigured ? maxSeats : Math.min(maxSeats, includedSeats);
          const seatOptions = activeRuntime?.workspace && selectableMaxSeats > 0
            ? Array.from({ length: (selectableMaxSeats - includedSeats) + 1 }, (_value, index) => includedSeats + index)
            : [];
          const billedSeats = activeRuntime?.workspace ? Math.max(effectiveSeats - includedSeats, 0) : 0;
          const additionalSeatMonthly = Number(activeRuntime?.additionalSeatPricePerMonth || 0) * billedSeats;
          const monthlyPrice = Number(activeRuntime?.monthlyPrice || 0) + additionalSeatMonthly;
          const workspaceCheckoutReady = seatPriceConfigured || maxSeats <= includedSeats;
          const checkoutPath = activeRuntime?.workspace && workspaceCheckoutReady
            ? buildCheckoutPath(activeRuntime?.checkoutPath, effectiveSeats)
            : (activeRuntime?.workspace ? null : activeRuntime?.checkoutPath);
          const activeTierLabel = i18n[`${activeTierKey}Label`] || activeTierKey;
          const detailPlanTitle = `${family.title} ${activeTierLabel}`;
          const detailCtaLabel = i18n.detailCta.replace('%{plan}', detailPlanTitle);
          const annualRuntime = activePlan ? findRuntime(activePlan, 12) : null;
          const featureRows = Array.isArray(family.featureRows) ? family.featureRows : [];
          const features = featureRows.map((feature) => ({
            ...feature,
            active: tierIndex(feature.minTier) <= tierIndex(activeTierKey)
          }));
          const cardClassName = activeRuntime
            ? 'panel panel--shadow subscription-tier-overview__card'
            : 'panel panel--shadow subscription-tier-overview__card subscription-tier-overview__card--unavailable';
          const soloAnnualBenefitCopy = (() => {
            if (family.key !== 'solo') {
              return null;
            }

            if (Number(selectedRuntimeMonths) === 12) {
              if (!activeRuntime?.trackVariantsIncluded) {
                return null;
              }

              return activeRuntime?.workspace ? i18n.soloAnnualBenefitIncluded : i18n.soloBasicAnnualBenefitIncluded;
            }

            if (!annualRuntime?.trackVariantsIncluded) {
              return null;
            }

            return annualRuntime?.workspace ? i18n.soloAnnualBenefitTeaser : i18n.soloBasicAnnualBenefitTeaser;
          })();
          const showSoloWorkspaceBenefitCopy = showWorkspaceBenefitHints && family.key === 'solo' && activeRuntime?.workspace && includedSeats > 0;
          const showSeatBenefitCopy = showWorkspaceBenefitHints && seatOptions.length > 1;

          return (
            <section key={family.key} className={cardClassName}>
              <div className='subscription-tier-overview__card-head'>
                <h3 className='subscription-tier-overview__title'>
                  <span>{family.title}</span>{' '}
                  <span className='subscription-tier-overview__title-tier'>{activeTierLabel}</span>
                </h3>
                <div className='subscription-tier-overview__price'>
                  {activeRuntime ? (
                    <>
                      <span className='subscription-tier-overview__price-amount'>{formatMonthlyPrice(locale, monthlyPrice)}</span>
                      <span className='subscription-tier-overview__price-unit'>{`/ ${i18n.monthLabel}`}</span>
                    </>
                  ) : i18n.unavailableForRuntime}
                </div>
                {activeRuntime ? (
                  <div className='subscription-tier-overview__billing'>{`${i18n.billedEvery} ${runtimeLabel(i18n, selectedRuntimeMonths)}`}</div>
                ) : null}
                <p className='subscription-tier-overview__description'>{activePlan?.description || family.teaser}</p>
              </div>

              <div className='subscription-tier-overview__chips'>
                {plans.map((plan) => {
                  const tierKey = tierKeyForPlan(plan.key);
                  const tierLabel = i18n[`${tierKey}Label`];
                  const isActive = activePlan?.key === plan.key;

                  return (
                    <button
                      key={plan.key}
                      type='button'
                      className={isActive ? 'subscription-tier-overview__chip subscription-tier-overview__chip--active' : 'subscription-tier-overview__chip'}
                      onClick={() => selectPlan(family.key, plan.key)}
                    >
                      {tierLabel}
                    </button>
                  );
                })}
              </div>

              <div className='subscription-tier-overview__features'>
                {features.map((feature) => (
                  <div
                    key={feature.label}
                    className={feature.active ? 'subscription-tier-overview__feature subscription-tier-overview__feature--active' : 'subscription-tier-overview__feature subscription-tier-overview__feature--inactive'}
                  >
                    <span>{feature.label}</span>
                  </div>
                ))}
              </div>

              {showAnnualBenefitHints && soloAnnualBenefitCopy ? (
                <div className='subscription-tier-overview__subnote'>{soloAnnualBenefitCopy}</div>
              ) : null}

              <div className='subscription-tier-overview__footer'>
                {seatOptions.length > 1 ? (
                  <div className='subscription-tier-overview__seat-picker'>
                    <label htmlFor={`subscription-tier-seats-${family.key}`} className='subscription-tier-overview__seat-label'>
                      {i18n.seatsPickerLabel}
                    </label>
                    <select
                      id={`subscription-tier-seats-${selectionKey}`}
                      className='input'
                      value={effectiveSeats}
                      onChange={(event) => selectSeats(selectionKey, event.target.value)}
                    >
                      {seatOptions.map((seatCount) => (
                        <option key={seatCount} value={seatCount}>
                          {seatCount === includedSeats ? `${seatCount} (${i18n.minimumSeatLabel})` : seatCount}
                        </option>
                      ))}
                    </select>
                    <span className='subscription-tier-overview__seat-copy'>
                      {seatPriceConfigured
                        ? i18n.seatPriceHint.replace('%{price}', formatMonthlyPrice(locale, Number(activeRuntime?.additionalSeatPricePerMonth || 0)))
                        : i18n.seatPriceUnavailable}
                    </span>
                    {showSeatBenefitCopy ? (
                      <div className='subscription-tier-overview__seat-copy'>
                        <strong>{i18n.seatBenefitTitle}</strong>
                        <span>{i18n.seatBenefitDetail}</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {activeRuntime?.workspaceStealthCheckout ? (
                  <span className='subscription-tier-overview__seat-copy'>{i18n.workspaceCheckoutHint}</span>
                ) : null}
                {!activeRuntime?.workspaceStealthCheckout && seatOptions.length <= 1 ? <strong>{seatInfo.primary}</strong> : null}
                {!activeRuntime?.workspaceStealthCheckout && seatOptions.length <= 1 && seatInfo.secondary ? <span>{seatInfo.secondary}</span> : null}
                {activeRuntime?.workspace && seatOptions.length <= 1 && maxSeats > includedSeats ? (
                  <span className='subscription-tier-overview__seat-copy'>
                    {seatPriceConfigured
                      ? i18n.seatPriceHint.replace('%{price}', formatMonthlyPrice(locale, Number(activeRuntime?.additionalSeatPricePerMonth || 0)))
                      : i18n.seatPriceUnavailable}
                  </span>
                ) : null}
                {showSoloWorkspaceBenefitCopy ? (
                  <div className='subscription-tier-overview__seat-copy'>
                    <strong>{i18n.soloWorkspaceBenefitTitle}</strong>
                    <span>{i18n.soloWorkspaceBenefitDetail}</span>
                  </div>
                ) : null}
                {checkoutPath ? (
                  <a href={checkoutPath} className='button button--tertiary button--full subscription-tier-overview__cta'>
                    {i18n.checkoutCta}
                  </a>
                ) : null}
                {activePlan?.detailPath ? (
                  <a href={activePlan.detailPath} className='button button--ghost-tertiary button--full subscription-tier-overview__detail-cta'>
                    {detailCtaLabel}
                  </a>
                ) : null}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

SubscriptionTierOverview.defaultProps = {
  locale: 'de',
  families: [],
  i18n: {}
};

let elements = document.querySelectorAll('[data-react-component=SubscriptionTierOverview]');

elements.forEach(function(element) {
  let reactProps = parseReactProps(element.dataset.reactProps);

  ReactDOM.render(
    <React.StrictMode>
      <SubscriptionTierOverview {...reactProps} />
    </React.StrictMode>,
    element
  );
});

export default SubscriptionTierOverview;
